import { Box, Typography, IconButton } from '@mui/joy';
import { useNavigate } from 'react-router-dom';
import LaunchIcon from '@mui/icons-material/Launch';
import { useState, useEffect, memo, useCallback } from 'react';
import { useConfigContext } from '@mono-farmart-web/farmart-ai';
import { SAFE_AREA_TOP } from '@mono-farmart-web/ui-components/component/UnifiedHeader/safeArea';

const useChatStatus = () => {
	const [chatStatus, setChatStatus] = useState<string>('idle');
	const { chatManager } = useConfigContext();

	useEffect(() => {
		const originalOnStatusChanged = chatManager?.onStatusChanged;
		chatManager.onStatusChanged = (status: string) => {
			setChatStatus(status);
			originalOnStatusChanged?.(status);
		};
	}, [chatManager]);

	return chatStatus;
};

const Header = memo(
	({ headerText, chatId }: { headerText: string; chatId: string }) => {
		const navigate = useNavigate();
		const chatStatus = useChatStatus();

		// Memoize the disabled state to prevent unnecessary re-renders
		const isLaunchIconDisabled =
			chatStatus === 'submitted' || chatStatus === 'streaming';

		const handleLaunchClick = useCallback(() => {
			if (!isLaunchIconDisabled) {
				navigate(`/ai/c/${chatId}`);
			}
		}, [isLaunchIconDisabled, navigate, chatId]);

		return (
			<Box
				sx={{
					position: 'sticky',
					top: 0,
					zIndex: 10,
					backgroundColor: 'white',
					borderBottom: '1px solid #E0E0E0',
					paddingTop: SAFE_AREA_TOP,
					boxSizing: 'border-box',
				}}
			>
				{/* Header Content */}
				<Box
					sx={{
						p: '12px 20px',
						display: 'flex',
						justifyContent: 'center',
						alignItems: 'center',
						cursor: 'pointer',
						position: 'relative',
					}}
				>
					<Typography
						level='h4'
						sx={{
							color: '#171A1C',
							fontFamily: 'NotoSans-SemiBold',
							fontSize: '14px',
							fontStyle: 'normal',
							fontWeight: 600,
							lineHeight: '17px',
							textAlign: 'center',
							maxWidth: 'calc(100% - 60px)',
						}}
					>
						{headerText}
					</Typography>
					<Box
						sx={{
							position: 'absolute',
							right: 32,
							bottom: 12,
							width: '24px',
							height: '24px',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							cursor: 'pointer',
							flexShrink: 0,
						}}
					>
						<IconButton
							onClick={handleLaunchClick}
							disabled={isLaunchIconDisabled}
							sx={{
								p: 0,
								opacity: isLaunchIconDisabled ? 0.5 : 1,
								'&:hover': {
									backgroundColor: 'transparent',
								},
								'&:disabled': {
									opacity: 0.3,
									cursor: 'not-allowed',
								},
							}}
						>
							<LaunchIcon sx={{ fontSize: '24px', color: '#171A1C' }} />
						</IconButton>
					</Box>
				</Box>
			</Box>
		);
	},
);

Header.displayName = 'Header';

export default Header;
